import { useState } from "react";
import { Crumbs } from "../components/Bits";
import { Head } from "../components/Head";
import { useData } from "../lib/data";
import { amount, copy, store, toast } from "../lib/util";

const DAYS = ["Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag", "Lørdag", "Søndag"];
const AIS = [["G", "Grønt og frugt"], ["K", "Kød og fisk"], ["M", "Mejeri og æg"], ["F", "Frost"], ["O", "Kolonial og andet"]];
const initPlan = () => { const p = store.get("plan", Array(7).fill("")); return Array.isArray(p) && p.length === 7 ? p : Array(7).fill(""); };

function shoppingList(plan, people, BY) {
  const agg = {}; let cost = 0, meals = 0;
  plan.forEach(s => {
    const r = BY[s]; if (!r) return; meals++; cost += r.price * people;
    r.ingredients.forEach(({ amount: a, unit: u, name: nm, aisle: ais }) => {
      const k = nm + "|" + u + "|" + ais;
      agg[k] = agg[k] || { a: 0, u, nm, ais, free: a === null };
      if (a !== null) agg[k].a += a * people / (r.base || 4);
    });
  });
  const items = Object.entries(agg);
  const groups = AIS.map(([code, name]) => [name, items.filter(([, x]) => x.ais === code).sort((a, b) => a[1].nm.localeCompare(b[1].nm, "da"))])
    .filter(([, its]) => its.length);
  return { groups, cost, meals, empty: !items.length };
}
const label = x => (x.free ? "" : amount(x.a, x.u) + " ") + x.nm;

export default function Plan() {
  const { recipes, BY } = useData();
  const [plan, setPlan] = useState(initPlan);
  const [people, setPeople] = useState(() => Math.min(12, Math.max(1, +store.get("people", 4) || 4)));
  const [done, setDone] = useState(() => store.get("done", {}));
  const [mode, setMode] = useState("alle");
  const opts = [...recipes].sort((a, b) => a.title.localeCompare(b.title, "da"));
  const save = (p, n) => { setPlan(p); setPeople(n); store.set("plan", p); store.set("people", n); };
  const { groups, cost, meals, empty } = shoppingList(plan, people, BY);

  const suggest = () => {
    let pool = recipes.filter(r => mode === "billig" ? r.price <= 12 : mode === "hurtig" ? r.time <= 30 : mode === "vegetar" ? r.tags.includes("Vegetar") : true);
    pool = pool.filter(r => !plan.includes(r.slug)).sort(() => Math.random() - 0.5);
    save(plan.map(s => s || (pool.length ? pool.pop().slug : "")), people);
  };
  const clear = () => { const d = {}; setDone(d); store.set("done", d); save(Array(7).fill(""), people); toast("Madplanen er ryddet"); };
  const text = () => {
    const lines = [];
    groups.forEach(([name, its]) => { if (lines.length) lines.push(""); lines.push(name + ":"); its.forEach(([, x]) => lines.push("- " + label(x).trim())); });
    return "Indkøbsliste\n\n" + lines.join("\n");
  };
  const onCopy = async () => toast((await copy(text())) ? "Indkøbslisten er kopieret" : "Kunne ikke kopiere");
  const onShare = async () => {
    if (navigator.share) { try { await navigator.share({ title: "Indkøbsliste", text: text() }); } catch (e) {} }
    else { await copy(text()); toast("Indkøbslisten er kopieret"); }
  };
  const tick = (k, v) => { const d = { ...done, [k]: v }; setDone(d); store.set("done", d); };

  return (
    <>
      <Head title="Madplan og indkøbsliste" path="madplan/" desc="Lav din ugentlige madplan, og få en samlet indkøbsliste sorteret efter afdeling og en anslået pris. Gratis." />
      <Crumbs trail={[[null, "Madplan"]]} />
      <div className="wrap" id="plan">
        <div className="ptop"><h1>Din madplan</h1><p className="lead">Vælg en opskrift til hver dag, eller få et forslag. Indkøbslisten opdateres automatisk og gemmes på din enhed.</p></div>
        <div className="pwrap">
          <div className="pbox">
            <div className="stepper"><span>Antal personer</span>
              <button type="button" id="pm" aria-label="Færre personer" onClick={() => save(plan, Math.max(1, people - 1))} data-testid="plan-people-minus">−</button>
              <b id="pn" aria-live="polite" data-testid="plan-people-count">{people}</b>
              <button type="button" id="pp" aria-label="Flere personer" onClick={() => save(plan, Math.min(12, people + 1))} data-testid="plan-people-plus">+</button></div>
            <div id="prow">{DAYS.map((d, i) => {
              const r = BY[plan[i]];
              return (
                <div className="prow" key={d}><b>{d}</b>
                  <select data-i={i} aria-label={`Opskrift til ${d.toLowerCase()}`} value={plan[i] || ""} data-testid={`plan-day-select-${i}`}
                    onChange={e => { const p = [...plan]; p[i] = e.target.value; save(p, people); }}>
                    <option value="">Vælg opskrift</option>
                    {opts.map(o => <option key={o.slug} value={o.slug}>{o.emoji} {o.title}</option>)}
                  </select>
                  <a hidden={!r} href={r ? `/opskrifter/${r.slug}/` : undefined} data-testid={`plan-day-open-${i}`}>Åbn</a></div>
              );
            })}</div>
            <div id="ptot" className="totals" hidden={!meals} data-testid="plan-totals">
              {meals ? <>{meals} aftensmåltider til {people} {people === 1 ? "person" : "personer"}. Anslået pris: <b>{Math.round(cost)} kr.</b></> : null}
            </div>
            <div className="fld" style={{ marginTop: 14 }}><label htmlFor="pmode">Forslag til tomme dage</label>
              <select id="pmode" value={mode} onChange={e => setMode(e.target.value)} data-testid="plan-mode-select">
                <option value="alle">Alle opskrifter</option><option value="billig">Kun billige (højst 12 kr. pr. person)</option>
                <option value="hurtig">Kun hurtige (højst 30 min)</option><option value="vegetar">Kun vegetar</option>
              </select></div>
            <div className="bar"><button className="btn" type="button" id="suggest" onClick={suggest} data-testid="plan-suggest-button">Foreslå en uge</button>
              <button className="btn ghost" type="button" id="clear" onClick={clear} data-testid="plan-clear-button">Ryd</button></div>
          </div>
          <div className="pbox shop"><h2>Indkøbsliste</h2>
            <div id="plist" data-testid="shopping-list">
              {empty ? <p className="none">Vælg opskrifter til ugens dage, så dukker indkøbslisten op her.</p>
                : groups.map(([name, its]) => [
                  <div className="aisle" key={name}>{name}</div>,
                  ...its.map(([k, x]) => (
                    <label key={k}><input type="checkbox" data-k={k} checked={!!done[k]} onChange={e => tick(k, e.target.checked)} />
                      <span>{x.free ? "" : <><b>{amount(x.a, x.u)}</b> </>}{x.nm}</span></label>
                  ))])}
            </div>
            <div className="bar noprint"><button className="btn" type="button" id="copy" onClick={onCopy} data-testid="plan-copy-button">Kopiér listen</button>
              <button className="btn ghost" type="button" id="shareList" onClick={onShare} data-testid="plan-share-button">Del</button>
              <button className="btn ghost" type="button" id="printList" onClick={() => window.print()} data-testid="plan-print-button">Print</button></div>
          </div>
        </div>
      </div>
    </>
  );
}
